import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import {
  Activity,
  Users,
  Download,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  XCircle,
  Loader2,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useToast } from '../../components/common/ToastContainer';
import { USER_TYPES } from '../../utils/constants';
import { formatDate } from '../../utils/formatDate';
import Tabs, { type Tab } from '../../components/common/Tabs';
import auditService from '../../services/audit.service';
import { useTenantStore } from '../../stores/tenantStore';
import { describeSignInRoutingActivity, isSignInRoutingActivity, signInRoutingActivityTone } from '../../utils/signInRouting';

// ==========================================
// Activity Log Tab
// ==========================================

const ActivityLogTab = () => {
  const { user } = useAuthStore();
  const toast = useToast();
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
  const { selectedTenantId } = useTenantStore();

  const effectiveTenantId = isSuperAdmin ? selectedTenantId : '';
  const needsTenant = isSuperAdmin && !selectedTenantId;

  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const pageSize = 20;

  const fetchData = useCallback(async (isBackground = false) => {
    if (needsTenant) { setItems([]); setTotal(0); setTotalPages(1); setLoading(false); return; }
    try {
      if (!isBackground) setLoading(true);
      const options = isBackground ? { showLoading: false } : undefined;
      const result: any = await auditService.listActivity({
        page,
        pageSize,
        search: search || undefined,
        tenantId: effectiveTenantId || undefined,
        resourceType: resourceType || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      }, options);
      setItems(result.data?.items || []);
      setTotalPages(result.data?.pagination?.totalPages || 1);
      setTotal(result.data?.pagination?.total || 0);
    } catch {
      toast.error('Load Failed', 'Failed to load activity log');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [page, search, effectiveTenantId, needsTenant, resourceType, dateFrom, dateTo]);

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchData(true), 30000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchData]);

  const handleExport = async () => {
    if (needsTenant) { toast.error('Select a Tenant', 'Choose a tenant in the top bar to export.'); return; }
    try {
      const response: any = await auditService.exportActivity({
        search: search || undefined,
        tenantId: effectiveTenantId || undefined,
        resourceType: resourceType || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      const blob = response instanceof Blob ? response : new Blob([response], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-log-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export Complete', 'Activity log downloaded');
    } catch {
      toast.error('Export Failed', 'Failed to export activity log');
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === 'success') {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Success</span>;
    }
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Failure</span>;
  };

  const getActionBadge = (action: string) => {
    // Identity providers and sign-in routing (doc 28d §10.3): a readable label,
    // the raw action on hover.
    if (isSignInRoutingActivity(action)) {
      const label = describeSignInRoutingActivity({ action, details: null, resource_name: null })?.label ?? action;
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${signInRoutingActivityTone(action)}`} title={action}>
          {label}
        </span>
      );
    }
    const colors: Record<string, string> = {
      create: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
      revoke: 'bg-orange-100 text-orange-800',
      assign: 'bg-purple-100 text-purple-800',
      unassign: 'bg-yellow-100 text-yellow-800',
      sync: 'bg-indigo-100 text-indigo-800',
      export: 'bg-gray-100 text-gray-800',
    };
    const cls = colors[action] || 'bg-gray-100 text-gray-800';
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{action}</span>;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search actions, resources, users..."
                className="input-base w-full pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Resource Type</label>
            <select
              value={resourceType}
              onChange={(e) => { setResourceType(e.target.value); setPage(1); }}
              className="input-base w-full"
            >
              <option value="">All Resources</option>
              <option value="edge">Edge</option>
              <option value="user">User</option>
              <option value="connector">Connector</option>
              <option value="wireguard">WireGuard</option>
              <option value="tenant">Tenant</option>
              <option value="role">Role</option>
              <option value="config_template">Config Template</option>
              <option value="peering">Peering</option>
              <option value="acl">ACL</option>
              <option value="access_identity_provider">Identity Provider</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="input-base w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="input-base w-full"
            />
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <label className="inline-flex items-center space-x-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span>Auto-refresh (30s)</span>
          </label>
          <div className="flex items-center space-x-2">
            <button onClick={() => fetchData(false)} className="btn-secondary inline-flex items-center space-x-2">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button onClick={handleExport} className="btn-secondary inline-flex items-center space-x-2">
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    {needsTenant ? 'Select a tenant from the top bar to view the audit log.' : 'No activity found'}
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <Fragment key={item.id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedRow(expandedRow === item.id ? null : item.id)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(item.created_at, dateSettings)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {item.user_email || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getActionBadge(item.action)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <span className="font-medium">{item.resource_type}</span>
                        {item.resource_id && (
                          <span className="text-gray-400 ml-1 text-xs font-mono">
                            {item.resource_id.length > 12 ? `${item.resource_id.slice(0, 12)}...` : item.resource_id}
                          </span>
                        )}
                        {isSignInRoutingActivity(item.action) && (
                          <span className="block text-xs text-gray-700" data-testid="audit-idp-summary">
                            {describeSignInRoutingActivity(item)?.summary}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(item.status)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {item.ip_address || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {item.details ? (
                          expandedRow === item.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : null}
                      </td>
                    </tr>
                    {expandedRow === item.id && item.details && (
                      <tr key={`${item.id}-details`}>
                        <td colSpan={7} className="px-6 py-3 bg-gray-50">
                          <div className="text-sm">
                            <div className="flex items-center space-x-4 mb-2 text-xs text-gray-500">
                              <span>{item.http_method} {item.http_path}</span>
                              <span>HTTP {item.http_status}</span>
                            </div>
                            <div className="bg-white rounded border border-gray-200 p-3 font-mono text-xs max-h-60 overflow-auto">
                              {typeof item.details === 'object' ? (
                                <div className="space-y-1">
                                  {Object.entries(item.details).map(([key, value]) => (
                                    <div key={key} className="flex">
                                      <span className="text-gray-500 w-40 flex-shrink-0">{key}:</span>
                                      <span className="text-gray-900 break-all">
                                        {value === '[REDACTED]' ? (
                                          <span className="text-red-500 italic">[REDACTED]</span>
                                        ) : typeof value === 'object' ? (
                                          JSON.stringify(value, null, 2)
                                        ) : (
                                          String(value)
                                        )}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <pre>{JSON.stringify(item.details, null, 2)}</pre>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-600">
            <span>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-2">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================
// Sessions Tab
// ==========================================

const SessionsTab = () => {
  const { user } = useAuthStore();
  const toast = useToast();
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
  const { selectedTenantId } = useTenantStore();

  const effectiveTenantId = isSuperAdmin ? selectedTenantId : '';
  const needsTenant = isSuperAdmin && !selectedTenantId;

  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const pageSize = 20;

  const fetchData = useCallback(async (isBackground = false) => {
    if (needsTenant) { setItems([]); setTotal(0); setTotalPages(1); setLoading(false); return; }
    try {
      if (!isBackground) setLoading(true);
      const options = isBackground ? { showLoading: false } : undefined;
      const result: any = await auditService.listSessions({
        page,
        pageSize,
        search: search || undefined,
        tenantId: effectiveTenantId || undefined,
        status: statusFilter || undefined,
      }, options);
      setItems(result.data?.items || []);
      setTotalPages(result.data?.pagination?.totalPages || 1);
      setTotal(result.data?.pagination?.total || 0);
    } catch {
      toast.error('Load Failed', 'Failed to load sessions');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [page, search, effectiveTenantId, needsTenant, statusFilter]);

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const handleRevoke = async (sessionId: string) => {
    if (!confirm('Revoke this session? The user will be logged out.')) return;
    try {
      await auditService.revokeSession(sessionId);
      toast.success('Session Revoked', 'The session has been revoked');
      fetchData();
    } catch {
      toast.error('Revoke Failed', 'Failed to revoke session');
    }
  };

  const handleExport = async () => {
    if (needsTenant) { toast.error('Select a Tenant', 'Choose a tenant in the top bar to export.'); return; }
    try {
      const response: any = await auditService.exportSessions({
        search: search || undefined,
        tenantId: effectiveTenantId || undefined,
        status: statusFilter || undefined,
      });
      const blob = response instanceof Blob ? response : new Blob([response], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sessions-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export Complete', 'Sessions downloaded');
    } catch {
      toast.error('Export Failed', 'Failed to export sessions');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      Active: 'bg-green-100 text-green-800',
      Expired: 'bg-gray-100 text-gray-800',
      Revoked: 'bg-red-100 text-red-800',
    };
    const cls = styles[status] || 'bg-gray-100 text-gray-800';
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{status}</span>;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search by user, IP, user agent..."
                className="input-base w-full pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="input-base w-full"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <button onClick={() => fetchData(false)} className="btn-secondary inline-flex items-center space-x-2">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button onClick={handleExport} className="btn-secondary inline-flex items-center space-x-2">
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Agent</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Activity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    {needsTenant ? 'Select a tenant from the top bar to view sessions.' : 'No sessions found'}
                  </td>
                </tr>
              ) : (
                items.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <div className="text-gray-900">{s.userEmail || '-'}</div>
                      {s.userName && <div className="text-xs text-gray-500">{s.userName}</div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{s.ipAddress || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate" title={s.userAgent}>
                      {s.userAgent || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(s.createdAt, dateSettings)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(s.lastActivity, dateSettings)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(s.expiresAt, dateSettings)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getStatusBadge(s.status)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {s.status === 'Active' && (
                        <button
                          onClick={() => handleRevoke(s.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium inline-flex items-center space-x-1"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          <span>Revoke</span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-600">
            <span>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-2">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================
// Main AuditLog Page
// ==========================================

const AuditLog = () => {
  const tabs: Tab[] = [
    {
      id: 'activity',
      label: 'Activity Log',
      icon: Activity,
      content: <ActivityLogTab />,
    },
    {
      id: 'sessions',
      label: 'Sessions',
      icon: Users,
      content: <SessionsTab />,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
        <p className="text-sm text-gray-500 mt-1">
          Platform-wide activity and session monitoring
        </p>
      </div>

      <Tabs tabs={tabs} defaultTab="activity" />
    </div>
  );
};

export default AuditLog;
